import { CSSProperties } from 'react';
import { connect, ConnectedComponent } from 'react-redux';
import {
  addEntityAction,
  multiAddEntityAction,
  deleteEntityAction,
  editEntityAction,
  getEntitiesAction,
  notifyMessageEntityAction,
  voidEntitiesAction
} from '../actions/entitiesActions';
import { ColumnType, WebEntity, WebEntityName } from '../entities/types';
import {
  ADD_FAIL_ENTITY,
  AUTH_ERROR,
  DELETE_FAIL_ENTITY,
  EDIT_FAIL_ENTITY,
  NOTIFY_MESSAGE_ENTITY
} from '../reducers/types';
import { AppState } from '../store';
import { BasicEntity } from '../types';
import GenericCRUD from './generics/GenericCRUD';
import {
  AdditionalTableActionsFunc,
  AdditionalTableProps,
  AdditionalTopTableComponents,
  BodyRowProps,
  CRUDAllowedActions,
  InitialState
} from './types';
import { MRT_RowData } from 'material-react-table';

interface OptionalParams<T> {
  title: string;
  GETPath: string;
  dummy: boolean;
  serverSidePagination: boolean;
  enablePagination: boolean;
  fetchByNRows: number;
  limitRows: number;
  query: any;
  additionalTableProps: AdditionalTableProps<T>;
  additionalTableActions: AdditionalTableActionsFunc<T>;
  allowedActions: Partial<CRUDAllowedActions<T>>;
  preProcessEntityList: (entityList: Array<any>) => Array<any>;
  customExport?: {
    exportDataHandler: (when?: string) => Promise<any[]>;
    columnTypes: ColumnType[];
    fileName?: string;
  };
  initialState: InitialState;
  enableColumnVirtualization?: boolean;
  enableRowVirtualization?: boolean;
  optionsPagination?: {
    initialStatePagination?: { pagination: { pageSize: number; pageIndex: number } };
  };
  columnsHeaderStyle?: CSSProperties;
  style: CSSProperties;
  additionalTopTableActions?: AdditionalTopTableComponents[];
  enableStickyFooter?: boolean;
  bodyRowProps?: BodyRowProps;
}

interface CRUDParams<T> extends Partial<OptionalParams<T>> {
  webEntity: WebEntity<T>;
}

/**
 * Function to generate CRUD connected with react-redux for an specified entity.
 *
 * @param {WebEntity<T>}  webEntity: The web entity that describe the CRUD to create
 * @param {string}   title: title of list
 * @param {string}   GETPath: an aditional path to get the element list /api/{entityName}/{GETPath}
 * @param {boolean}  serverSidePagination: bool - get paginated data from the endpoint
 * @param {number}   fetchByNRows: fetch the data from the endpoint by N rows until get all the data
 * @param {number}   limitRows: fetch a limit of N of rows
 * @param {any}      query: a query to filter the elements shown in the list /api/{entityName}/{GETPath}/{filter}
 *                   recives an object and automatically parse it to url query format.
 * @param {AdditionalTableProps} additionalTableProps: object with customization options for GenericTable
 * @param {AdditionalTableActionsFunc} additionalTableActions: function to render into the actions for selected items in the table
 * @param {CRUDAllowedActions} allowedActions: Allow you to enable or disable CRUD actions: (add, delete, export and select)
 * @param {any}      style: style for CRUD cointainer
 */
export const generateCRUD = <T extends BasicEntity>({
  webEntity,
  title,
  GETPath,
  dummy,
  serverSidePagination,
  enablePagination,
  fetchByNRows,
  limitRows,
  query,
  additionalTableProps,
  additionalTableActions,
  allowedActions,
  preProcessEntityList,
  customExport,
  initialState,
  enableColumnVirtualization,
  enableRowVirtualization,
  optionsPagination,
  columnsHeaderStyle,
  style,
  additionalTopTableActions,
  enableStickyFooter,
  bodyRowProps
}: CRUDParams<T>): ConnectedComponent<any, any> => {
  allowedActions = {
    add: true,
    delete: true,
    edit: false,
    export: false,
    multiLineForm: false,
    select: false,
    ...allowedActions
  };

  const mapStateToProps = ({ entities, auth, error }: AppState) => ({
    entities,
    error,
    auth
  });

  // Generate Action Functions for entity
  const getEntities =
    webEntity.endpoint && !dummy
      ? getEntitiesAction(webEntity.name as WebEntityName, webEntity.endpoint, {
          GETPath,
          query,
          fetchByNRows,
          limitRows
        })
      : voidEntitiesAction(webEntity.name as WebEntityName);

  const deleteEntity = deleteEntityAction(webEntity.name as WebEntityName, webEntity.endpoint);
  const addEntity = addEntityAction(webEntity.name as WebEntityName, webEntity.endpoint);
  const multiAddEntity = multiAddEntityAction(webEntity.name as WebEntityName, webEntity.endpoint);
  const editEntity = editEntityAction(webEntity.name as WebEntityName, webEntity.endpoint);
  const notifyMessageEntity = notifyMessageEntityAction(webEntity.name as WebEntityName);

  const DEL_FAIL_FOR_THIS_ENTITY = `${DELETE_FAIL_ENTITY}${webEntity.name.toUpperCase()}`;
  const ADD_FAIL_FOR_THIS_ENTITY = `${ADD_FAIL_ENTITY}${webEntity.name.toUpperCase()}`;
  const EDIT_FAIL_FOR_THIS_ENTITY = `${EDIT_FAIL_ENTITY}${webEntity.name.toUpperCase()}`;
  const NOTIFY_MESSAGE_FOR_THIS_ENTITY = `${NOTIFY_MESSAGE_ENTITY}${webEntity.name.toUpperCase()}`;

  const signalsToWatch = [
    AUTH_ERROR,
    DEL_FAIL_FOR_THIS_ENTITY,
    ADD_FAIL_FOR_THIS_ENTITY,
    EDIT_FAIL_FOR_THIS_ENTITY,
    NOTIFY_MESSAGE_FOR_THIS_ENTITY
  ];
  const mapDispatchToProps = (
    dispatch: any,
    ownProps = {
      webEntity,
      title,
      signalsToWatch,
      additionalTableProps,
      additionalTableActions,
      serverSidePagination,
      allowedActions,
      preProcessEntityList,
      customExport,
      initialState,
      enableColumnVirtualization,
      enableRowVirtualization,
      enablePagination,
      optionsPagination,
      columnsHeaderStyle,
      style,
      additionalTopTableActions,
      enableStickyFooter,
      bodyRowProps
    }
  ) => ({
    webEntity: ownProps.webEntity,
    title: ownProps.title !== undefined ? ownProps.title : ownProps.webEntity.name,
    signalsToWatch: ownProps.signalsToWatch,
    additionalTableProps: ownProps.additionalTableProps,
    additionalTableActions: ownProps.additionalTableActions,
    serverSidePagination: ownProps.serverSidePagination,
    enablePagination: ownProps.enablePagination,
    allowedActions: ownProps.allowedActions,
    preProcessEntityList: ownProps.preProcessEntityList,
    customExport: ownProps.customExport,
    columnsHeaderStyle: ownProps.columnsHeaderStyle,
    style: ownProps.style,
    initialState: ownProps.initialState,
    additionalTopTableActions: ownProps.additionalTopTableActions,
    enableColumnVirtualization: ownProps.enableColumnVirtualization,
    enableRowVirtualization: ownProps.enableRowVirtualization,
    optionsPagination: ownProps.optionsPagination,
    enableStickyFooter: ownProps.enableStickyFooter,
    bodyRowProps: ownProps.bodyRowProps,
    getEntities: () => dispatch(getEntities()),
    deleteEntity: (id: string[]) => dispatch(deleteEntity(id)),
    addEntity: (entity: T | T[]) => dispatch(addEntity(entity)),
    multiAddEntity: (entity: T | T[]) => dispatch(multiAddEntity(entity)),
    editEntity: (entity: MRT_RowData) => dispatch(editEntity(entity)),
    notifyMessageEntity: (msg: string) => dispatch(notifyMessageEntity(msg))
  });

  // This connect receives
  // a function to map the redux states into props for this component,
  // a props list which are additional props
  return connect(mapStateToProps, mapDispatchToProps)(GenericCRUD);
};
